import { useState, createContext } from "react";

/**
 * @typedef {Object} Settings
 * @property {boolean} sendCopyOption - Whether to show the send copy option.
 * @property {boolean} readAloud - Whether to enable read aloud functionality.
 * @property {boolean} aiInterviewer - Whether to enable AI interviewer.
 * @property {boolean} progressBar - Whether to show the progress bar.
 * @property {boolean} miniGame - Whether to enable the mini game.
 * @property {boolean} addressStep - The research asks for the address first (Research.AskAddressFirst); every save carries the flag so the server links the household.
 * @property {boolean} nameStep - The research asks for the name first (Research.AskNameFirst); every save carries the flag so the server links the participant once staff have matched the session.
 */

/**
 * Context for managing questionnaire state.
 * @typedef {Object} QuestionnaireContextType
 * @property {number} fieldIndex - The current field index.
 * @property {React.Dispatch<React.SetStateAction<number>>} setFieldIndex - 
 * Function to set the current field index.
 * @property {boolean} gamificationEnabled - Whether gamification is enabled.
 * @property {React.Dispatch<React.SetStateAction<boolean>>} setGamificationEnabled - 
 * Function to set gamification enabled state.
 * @property {Settings} settings - The settings object.
 * @property {React.Dispatch<React.SetStateAction<Settings>>} setSettings - 
 * Function to update the settings object.
 * @property {boolean} isTransitioning - Whether a transition is currently happening.
 * @property {React.Dispatch<React.SetStateAction<boolean>>} setIsTransitioning - 
 * Function to set the transition state.
 * @property {number} transitionDuration - Timeout duration for the next button in milliseconds.
 * @property {React.Dispatch<React.SetStateAction<number>>} setTransitionDuration - 
 * Function to set the next button timeout duration.
 */
export const QuestionnaireContext = createContext(
    /** @type {QuestionnaireContextType | null} */ (null)
);

let timeout = null;

export const QuestionnaireProvider = ({
    children
}) => {
    // const [questionnaireId, setQuestionnaireId] = useState(null);
    const [gamificationEnabled, setGamificationEnabled] = useState(false);
    const [fieldIndex, _setFieldIndex] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [transitionDuration, setTransitionDuration] = useState(0);

    const setFieldIndex = (index) => {

        if(timeout) clearTimeout(timeout);

        /**
         * The delay is only applied when gamification is enabled (animation is played). 
         * If gamification is disabled, the field index is updated immediately without any delay. 
         */
        if (!gamificationEnabled) {
            _setFieldIndex(index);
        }

        /* When going back, don't apply the transition delay */
        if (index < fieldIndex) {
            _setFieldIndex(index);
            return;
        }
        
        setIsTransitioning(true);
        
        timeout = setTimeout(() => {
            _setFieldIndex(index);
            setIsTransitioning(false);
            // setTransitionDuration(0); // Reset to 0 after transition
        }, transitionDuration);
    }

    /**
     * @type {[Settings, React.Dispatch<React.SetStateAction<Settings>>]}
     */
    const [settings, setSettings] = useState({
        sendCopyOption: false,
        readAloud: false,
        aiInterviewer: false,
        progressBar: false,
        miniGame: false,
        addressStep: false,
        nameStep: false,
    });

    return (
        <QuestionnaireContext.Provider
            value={{
                // questionnaireId,
                fieldIndex,
                setFieldIndex,
                gamificationEnabled,
                setGamificationEnabled,
                settings,
                setSettings,
                isTransitioning,
                setIsTransitioning,
                transitionDuration,
                setTransitionDuration
            }}>
            {children}
        </QuestionnaireContext.Provider>
    );
};
